/**
 * Level JSON v1: levels are data, not code (PRD §6). RULES_VERSION is the newest rules this
 * engine plays (roadmap-3k §8.1). Versions are additive: a bump adds mechanics or fields and
 * never changes how a level of an older version plays (the golden vectors guard that), so this
 * engine plays every level with `rulesVersion` in [1, RULES_VERSION] exactly as older engines did.
 */
export const RULES_VERSION = 2;
/** Rules version that introduced glue (Seoul, L461): a level with a glued tile needs at least this. */
export const GLUE_RULES = 2;
/** Rules version that introduced bagLock (Mexico City, L611): a level with `bagLock` needs at least this. */
export const BAGLOCK_RULES = 2;
/** Most locked bag slots a level may start with (roadmap-3k §3 lever 3 uses 1). */
export const MAX_BAG_LOCK = 2;
/** Rules version that introduced dispensers (London, L761): a level with `dispensers` needs at least this. */
export const DISPENSER_RULES = 2;
/** Most dispensers on one level, and the item range of one dispenser's stack (roadmap-3k §4). */
export const MAX_DISPENSERS = 2;
export const MIN_DISPENSER_ITEMS = 3;
export const MAX_DISPENSER_ITEMS = 8;
/** Rules version that introduced lockKey (Mumbai, L911): a level with `lockKey` needs at least this. */
export const LOCKKEY_RULES = 2;
/** Locked tiles on a lockKey level (roadmap-3k §4). */
export const MIN_LOCKS = 2;
export const MAX_LOCKS = 6;
/**
 * Rules version that introduced the clear goal (Istanbul "Closing time", L1061): a level with
 * `goal: 'clear'` needs at least this.
 */
export const CLEARGOAL_RULES = 2;
/**
 * Rules version that introduced the hidden line (Rio, L1211): a level with a `hidden` ticket line
 * needs at least this.
 */
export const HIDDENLINE_RULES = 2;
/**
 * Rules version that introduced the collect goal (Berlin "golden items", L1361): a level with
 * `goal: 'collect'` or a golden tile needs at least this.
 */
export const COLLECTGOAL_RULES = 2;
/** Golden board tiles on a collectGoal level (roadmap-3k §4). */
export const MIN_GOLDEN = 3;
export const MAX_GOLDEN = 9;
/** Rules version that introduced trays (Bangkok, L1511): a level with `trays` needs at least this. */
export const TRAY_RULES = 2;
/** Most trays (pre-made triples) on one level (roadmap-3k §4). */
export const MAX_TRAYS = 3;
/**
 * Rules version that introduced three customers (Rome, L1961): a level with three customers
 * (three ticket cards) needs at least this. One or two customers stay rules v1.
 */
export const THREECUSTOMERS_RULES = 2;
/** Most customers (ticket cards) on one level: two on rules v1, three from THREECUSTOMERS_RULES. */
export const MAX_CUSTOMERS = 3;
/** Rules version that introduced shelves (Lagos, L1661): a level with `shelves` needs at least this. */
export const SHELF_RULES = 2;
/** Most shelves on one level, and the item range of one shelf's row (roadmap-3k §4). */
export const MAX_SHELVES = 1;
export const MIN_SHELF_ITEMS = 4;
export const MAX_SHELF_ITEMS = 8;
export const GRID_W = 14; // half-tile units: 7 tile columns
export const GRID_H = 18; // 9 tile rows
export const MAX_LAYER = 3; // 4 layers (0..3)

export type Mechanic = 'clutter' | 'wrapped' | 'twoCustomers' | 'rush' | 'crates' | 'ice' | 'conveyor' | 'glue' | 'bagLock' | 'dispenser' | 'lockKey' | 'clearGoal' | 'hiddenLine' | 'collectGoal' | 'tray' | 'threeCustomers' | 'shelf';

export interface LevelTile {
  item: string;
  layer: number;
  x: number;
  y: number;
  wrapped?: boolean;
  ice?: number; // 1..2 extra taps
  /**
   * Glue pair id (rules v2, roadmap-3k §4): exactly two tiles share an id. They sit on the same
   * layer, orthogonally adjacent edge to edge (same y and |dx| = 2, or same x and |dy| = 2 in
   * half-tile units: the two 2×2 footprints share a full side), and carry no ice or wrap. Items
   * may differ. While both are on the board they are tapped together (see doTap).
   */
  glue?: number;
  /**
   * Padlock (rules v2, lockKey): the tile cannot be tapped (even uncovered) until a triple of the
   * level's key item pops; it still covers what lies under it. Only with `LevelV1.lockKey`; never on
   * a tile of the key item, and never with glue, ice or wrap.
   */
  locked?: true;
  /**
   * Golden marker (rules v2, collectGoal): this board piece must pop before the round can be won.
   * Only with `goal: 'collect'`; never with glue, ice, wrap or `locked`. The marker stays on this
   * piece when shuffle relabels items and while the piece is in the bag.
   */
  golden?: true;
}

export interface LevelCrate {
  x: number;
  y: number;
  layer: number;
  hp: number; // pops needed while uncovered
}

/**
 * Dispenser (rules v2, roadmap-3k §4): a machine on the board surface at one tile-sized cell
 * holding a stack of items, `items[0]` on top. It sits on `layer` like a tile: tiles above it that
 * overlap its footprint cover it, and while it holds any item it covers what it overlaps below.
 * Only the top item is shown and tappable (when the machine is uncovered); a tap collects it (a
 * normal collect into the bag) and shows the next. The empty machine is gone. A single-slot,
 * spatial form of the conveyor queue (see CompiledLevel.dispOf). Items are plain: never glued,
 * iced or wrapped.
 */
export interface LevelDispenser {
  x: number;
  y: number;
  layer: number;
  items: string[]; // top first
}

/**
 * Tray (rules v2, roadmap-3k §4, Bangkok L1511): a pre-made triple, three identical `item`s held
 * at one board cell. It sits on `layer` at `x`, `y` like a tile: tiles above it that overlap its
 * footprint cover it, and while it is on the board it covers what it overlaps below; the overlap
 * rule sees it as one piece. It compiles to three `Kind.Tray` pieces (see CompiledLevel.trayOf).
 * Tapping it while it is uncovered pops all three at once, as a triple, without using the bag
 * (event `tray`): it serves the ticket, counts for combos, coins, bagLock, lockKey and hiddenLine
 * like any pop, and needs no free slot. Its three items count in the level's item totals (each
 * type a multiple of 3, the ticket satisfiable) and piece count. Undo never returns it, the
 * magnet never takes it and shuffle never relabels it; an assist swap of its item relabels all
 * three together. It is never glued, iced, wrapped, locked or golden.
 */
export interface LevelTray {
  x: number;
  y: number;
  layer: number;
  item: string;
}

/**
 * Shelf (rules v2, roadmap-3k §4, Lagos L1661): a side shelf beside the board holding a row of
 * `items`, left to right: a lateral pile with deque access. It is not on the board grid (no
 * position, no cover, no overlap check), like the conveyor, and the whole row is visible: the
 * player can plan it. Only its two ends are tappable, the leftmost and the rightmost item still
 * on the shelf (one item left is both ends); a tap collects the item into the bag like any collect
 * (event `shelved`) and makes its neighbour the new end. It compiles to one `Kind.Shelf` piece
 * per item, in row order (see CompiledLevel.shelfOf). Undo returns an item to its own place in the
 * row, and it is an end again: undo is last in, first out, so the items beyond it on the side it
 * was taken from (all off the shelf then) are still off it. A neighbour that popped or was
 * magnetted meanwhile leaves a hole, which the ends skip. Only an end is magnetable; shuffle
 * never permutes shelf items; assist relabels them with every other piece. Its items count in the
 * level's item totals (each type a multiple of 3, the ticket satisfiable) and piece count, and in
 * a clear goal; they are never glued, iced, wrapped, locked or golden.
 */
export interface LevelShelf {
  items: string[]; // left to right
}

export interface OrderLine {
  item: string;
  count: number; // 3 | 6 | 9
  /**
   * Hidden line (rules v2, roadmap-3k §4, Rio L1211): the line is shown face down (the player sees
   * that it exists and its count, not its item) until it is revealed (event `lineRevealed`). At
   * most one line per level; it is the last line of a customer with at least 2 lines, its item is
   * on no other line, and it is revealed by the first pop (or magnet) that either completes the
   * line just before it on the same customer's ticket (its "revealer") or serves the hidden line
   * itself. Serving never waits for the reveal: a triple of the hidden item popped while the line
   * is face down serves it (and reveals it), exactly as on a face-up line, so play that could not
   * know the item is never punished and a replay needs no extra state. The magnet never serves a
   * face-down line; assist never swaps its item. Serving is irreversible, so nothing hides it again.
   */
  hidden?: true;
}

export interface Customer {
  id: string; // customer asset id, e.g. cus_ny_012
  lines: OrderLine[];
}

export interface LevelV1 {
  v: 1;
  id: string; // e.g. "ny-042"
  city: string;
  shop: string;
  number: number; // global level number within its city pack (1-based)
  /** the oldest engine (RULES_VERSION) that plays this level: the max over its mechanics and fields */
  rulesVersion: number;
  grid: { w: number; h: number };
  tiles: LevelTile[];
  crates?: LevelCrate[];
  conveyor?: { slots: number; items: string[] };
  /** dispensers (rules v2): 1..MAX_DISPENSERS machines, each MIN..MAX_DISPENSER_ITEMS items, top first */
  dispensers?: LevelDispenser[];
  /** trays (rules v2): 1..MAX_TRAYS pre-made triples, each at one board cell (pieces after every dispenser item) */
  trays?: LevelTray[];
  /** shelves (rules v2): 1..MAX_SHELVES side shelves, each MIN..MAX_SHELF_ITEMS items left to right (pieces after every tray piece) */
  shelves?: LevelShelf[];
  /**
   * One ticket card per customer: 1..2 on rules v1, up to MAX_CUSTOMERS (three, rules v2,
   * roadmap-3k §4 threeCustomers, Rome L1961). Only the count changes with a third customer: a
   * pop serves the first line needing its item across every card, in customer then line order.
   */
  customers: Customer[];
  bag: number;
  /**
   * Bag lock (rules v2, roadmap-3k §3 lever 3): this many of the `bag` slots (1..MAX_BAG_LOCK)
   * start locked, so the round starts with cap `bag - bagLock`. The first ticket line to be
   * completed (any line, any customer, by a pop or a magnet) unlocks them for the rest of the
   * round (event `bagUnlocked`). Serving is irreversible, so nothing re-locks them.
   */
  bagLock?: number;
  /**
   * Lock and key (rules v2, roadmap-3k §4, Mumbai L911): MIN_LOCKS..MAX_LOCKS tiles are `locked`.
   * The first triple of `key` (an item of the pile: a ticket or clutter item) to pop, by a tap or
   * a magnet, opens every lock for the rest of the round (event `unlocked`). Popping is
   * irreversible, so nothing re-locks them. The key is an item type: shuffle may move key labels
   * between unlocked tiles (never onto a locked one), and assist never swaps it.
   */
  lockKey?: { key: string };
  /**
   * Round goal (rules v2, roadmap-3k §4). Absent: the order goal (the round is won when the ticket
   * is complete). `'clear'` ("Closing time", Istanbul L1061): the round is won only when every
   * tile, conveyor item, dispenser item, tray piece and shelf item has popped (the bag is then empty too; crates are
   * obstacles, not items, and do not count). `'collect'` ("golden items", Berlin L1361): the
   * ticket and every golden board tile must have popped; a golden in the bag is not done. The
   * ticket still pays order-pop coins and its first completed line still opens a bagLock. On
   * clear and collect levels, completing it fires `orderDone` and does not necessarily win.
   */
  goal?: 'clear' | 'collect';
  rush?: { ms: number };
  mechanics: Mechanic[];
  par: number;
  reward: { coins: number };
  /**
   * GP-09 assist: ordered label swaps [orderItem, clutterItem]. Assist level k applies the
   * first k swaps: every tile of the two types trades labels, so each triple keeps its
   * positions and only which triples the ticket asks for moves (measured easier at generation).
   */
  assist?: [string, string][];
  /**
   * Lookalike substitutes (roadmap-3k §3 lever 2, §11): each clutter item the generator chose for
   * looking like a ticket item (GenSpec.lookalikes) → a substitute item, from another shop of the
   * same city (its art ships in the same pack), whose silhouette is least like every item of the
   * level. With lookalikes turned off under the accessibility setting the client draws the
   * substitute's art for that item; the item itself does not change. Presentation only: rounds,
   * replays and the solver never read it and it never changes play, so, like `stats` and
   * `generator`, it is not part of levelHash. validateLevel: keys are clutter items of the level,
   * values are not items of the level, and values are distinct.
   */
  lookalikes?: Record<string, string>;
  stats?: { greedyWinRate: number; intendedTaps: number; tiles: number; layers: number };
  generator?: { version: number; seed: number; band: [number, number]; template: string; parWidth?: number };
  hash?: string;
}

export const enum Status {
  Playing = 0,
  Won = 1,
  Failed = 2,
}

export const enum FailReason {
  None = 0,
  BagFull = 1,
  Timeout = 2,
  Stuck = 3,
}

export const enum Kind {
  Tile = 0,
  Crate = 1,
  Conveyor = 2,
  /** an item in a dispenser's stack (pieces after the conveyor's) */
  Dispenser = 3,
  /** one of a tray's three identical pieces (pieces after every dispenser item; only the first is a tap target) */
  Tray = 4,
  /** an item on a shelf's row (pieces after every tray piece; only the row's two ends are tap targets) */
  Shelf = 5,
}

export const enum Booster {
  Undo = 1,
  Shuffle = 2,
  Magnet = 4,
  ExtraSlot = 8,
}

export const enum Op {
  Tap = 0,
  Undo = 1,
  Shuffle = 2,
  Magnet = 3,
  ExtraSlot = 4,
  RescueSlot = 5,
  RescueTime = 6,
  Tick = 7,
}

export interface Action {
  op: Op;
  /** active-play milliseconds since round start (integer, monotonic) */
  t: number;
  /** tile index for Tap */
  arg?: number;
}

export interface RoundConfig {
  /**
   * Rescue offered after a bag-full or stuck fail when the goal's sets remaining are at most this
   * (GP-04.2, default 2): ticket sets on an order level, sets still on the board on a clear-goal
   * level, ticket sets plus unpopped golden pieces on a collect-goal level (see
   * goalSetsRemaining).
   */
  rescueThreshold: number;
  maxRescues: number;
  coinsOrderPop: number;
  coinsClutterPop: number; // GP-02.2: +5
  coinsSweep: number; // per tile left when the order completes (always 0 coins on a clear-goal win: nothing is left)
  rescueTimeMs: number; // GP-07.2: +30s
  shuffleTries: number;
  shuffleCheckRuns: number;
}

export const DEFAULT_ROUND_CONFIG: RoundConfig = {
  rescueThreshold: 2,
  maxRescues: 2,
  coinsOrderPop: 10,
  coinsClutterPop: 5,
  coinsSweep: 1,
  rescueTimeMs: 30000,
  shuffleTries: 8,
  shuffleCheckRuns: 200,
};

export interface RoundOutcome {
  result: 'win' | 'fail';
  taps: number;
  stars: number;
  baseCoins: number;
  tips: number;
  sweepCoins: number;
  coins: number;
  bestCombo: number;
  boosters: number;
  rescues: number;
  durationMs: number;
  orderPops: number;
  clutterPops: number;
  /**
   * The goal's sets remaining at the end (goalSetsRemaining): ticket sets not yet served on an
   * order level; on a clear-goal level the sets still on the board (items on the board, crates
   * excluded, divided by 3 rounded up); on a collect-goal level ticket sets plus golden pieces
   * not yet popped, 0 on a win.
   */
  setsRemaining: number;
  /** ticket items served (both goals) */
  itemsServed: number;
}

export type RoundEvent =
  | { type: 'collected'; tile: number; slot: number; bag: number[] }
  /**
   * A dispenser item left its machine: right after its `collected` (tap), or after the `magnet`
   * event that took it (then before `uncovered`). `tile` is the item's piece, `next` the piece now
   * on top of that dispenser (-1: the machine is empty and gone), `left` the items still in it.
   * A tap's `uncovered` that follows lists `next` when the machine is uncovered, and on the last
   * item the pieces the machine stood on that are now free.
   */
  | { type: 'dispensed'; dispenser: number; tile: number; next: number; left: number }
  | { type: 'popped'; item: number; tiles: number[]; chain: number; comboStep: number; coins: number }
  | { type: 'served'; customer: number; line: number; item: number; remaining: number }
  | { type: 'clutter'; item: number; coins: number }
  | { type: 'uncovered'; tiles: number[] }
  | { type: 'iceCracked'; tile: number; ice: number }
  | { type: 'crateHit'; crate: number; hp: number }
  /** a glued pair tap: `tile` is the tapped tile, `partner` the one it pulls along (two 'collected' follow) */
  | { type: 'glued'; tile: number; partner: number }
  /**
   * tray: a tap on tray `tray` (its index in `LevelV1.trays`) popped its three pieces `tiles`
   * (ascending: trayStart[tray], +1, +2) straight off the board, never through the bag. It is
   * the first event of that tap, fired once all three pieces are gone; then `uncovered` (the
   * pieces the tray freed, if any), then the pop exactly as a bag pop resolves it: `popped`
   * (`tiles` the same three pieces), `served`|`clutter`, any `bagUnlocked`, `unlocked`,
   * `lineRevealed` and `orderDone` it causes (never `golden`: a tray is never golden), its
   * `crateHit`s, then `won`/`failed` from the settle. No `collected`: the bag is untouched, so a
   * tray pop never fills it, and undo never returns a tray (it leaves no history entry). A tray's
   * three pieces are covered and freed together: an `uncovered` that frees a tray lists all three.
   */
  | { type: 'tray'; tray: number; tiles: [number, number, number] }
  /**
   * shelf: an item left shelf `shelf` (its index in `LevelV1.shelves`). On a tap it comes right
   * after the item's `collected` (as `dispensed` does), before that collect's pop events (`popped`
   * ...); a shelf item is no board cover, so no `uncovered` comes from it. On a magnet it comes
   * after the `magnet` event (and after any `dispensed`), before `uncovered`, one per shelf item
   * the magnet took (both ends of one shelf may go in one magnet: each event then reports the
   * shelf as the magnet leaves it). `tile` is the item's piece, `left` the items still on the
   * shelf, `ends` its new ends: the first and the last piece still on it (the same piece when one
   * is left, [-1, -1] when it is empty). Undo returns a shelf item with the plain `undone` event.
   */
  | { type: 'shelved'; shelf: number; tile: number; left: number; ends: [number, number] }
  /**
   * `partner`: a glued pair returned by the same undo (both back on the board; `bag` is after both).
   * A dispenser item goes back on top of its machine (the item it had revealed is hidden again).
   */
  | { type: 'undone'; tile: number; bag: number[]; partner?: number }
  | { type: 'shuffled'; changes: Array<[tile: number, item: number]> }
  | { type: 'magnet'; item: number; tiles: number[]; line: number; remaining: number }
  | { type: 'extraSlot'; cap: number }
  /**
   * bagLock: the locked slots open. Fires once per round, right after the `served` event that
   * brings a line's remaining to 0 for the first time (pop or magnet), before that pop's
   * `crateHit`s and any later `collected`/`won`/`failed`. `cap` is the bag capacity after unlocking.
   */
  | { type: 'bagUnlocked'; slots: number; cap: number; line: number }
  /**
   * lockKey: every lock opens. Fires once per round, in the pop (or magnet) of the first triple of
   * the key item `key`: right after that pop's `served`|`clutter` event (and its `bagUnlocked`, if
   * the same pop completes the first line), before its `crateHit`s and any later
   * `collected`/`won`/`failed`. `tiles`: the locked tiles (all still on the board), ascending.
   */
  | { type: 'unlocked'; key: number; tiles: number[] }
  /**
   * clearGoal/collectGoal: the ticket is complete, but the goal may still be open. Fires once per
   * round on either goal, in the pop (or magnet) whose `served` event brings the last open ticket
   * line to 0: after that pop's `served`, any `bagUnlocked`/`unlocked`/`lineRevealed`, and its
   * `golden` event, before its `crateHit`s and any later `collected`/`won`/`failed`. When the same
   * action also meets the goal, `won` follows later in the same event list. On clearGoal, `left`
   * is items still on the board at that moment (tiles, conveyor, dispenser, tray and shelf items; a glued
   * partner still to be collected by the same tap counts; crates do not). On collectGoal, `left`
   * is golden pieces not yet popped after this pop. An order level never fires it (it fires `won`
   * instead).
   */
  | { type: 'orderDone'; left: number }
  /**
   * hiddenLine: the face-down line is revealed. Fires once per round, only on a level with a hidden
   * line, in the pop (or magnet) whose `served` event either brings the revealer line (the one
   * before the hidden line on the same customer's ticket) to 0 or serves the hidden line itself
   * while it is face down (then that `served` names the still-hidden `line`). Order within that
   * pop: `served`, then any `bagUnlocked` and `unlocked` it causes, then `lineRevealed`, then its
   * `crateHit`s and any later `collected`/`won`/`failed`. It never shares an event list with
   * `orderDone` or `won`: a pop that reveals leaves the revealer or the hidden line open (the other
   * one completed it), and a clear-goal board cannot empty while a revealer or hidden-item triple
   * is still to pop. `customer` and `line` (the global line index, as in `served`) locate the
   * line, `item` is its item index.
   */
  | { type: 'lineRevealed'; customer: number; line: number; item: number }
  /**
   * collectGoal: one pop (tap or magnet) removed one or more golden pieces. Fires after that pop's
   * `served`|`clutter`, `bagUnlocked`, `unlocked` and `lineRevealed`, before `orderDone`,
   * `crateHit`, and any later `collected`/`won`/`failed`. `tiles` is the golden subset of the
   * triple, ascending; `left` is the number of golden pieces not yet popped after this pop.
   */
  | { type: 'golden'; tiles: number[]; left: number }
  | { type: 'rescued'; kind: 'slot' | 'time'; cap: number; deadline: number; returned: number[] }
  | { type: 'won'; outcome: RoundOutcome }
  | { type: 'failed'; reason: 'bagFull' | 'timeout' | 'stuck'; rescueEligible: boolean }
  | { type: 'illegal'; reason: string };
